'use client';

import { Button, Flex, Grid, SegmentedControl } from '@mantine/core';
import { groupBy, isDark, useReplaceQueryParams } from '@/utils/utils';
import { useEffect, useLayoutEffect, useState } from 'react';

import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import StatisticsCard from '@/features/Statistics/StatisticsCard';
import { mixpanelTrack } from '@/utils/mixpanel';
import { sendGTMEvent } from '@next/third-parties/google';
import { useGetStatisticsWithCategory } from '@/features/Statistics/hook/api';
import { useScrollInit } from '@/utils/hook';
import { useStatisticsListStyles } from './page.style';

const CATEGORY_LIST = [
  '프론트엔드',
  '백엔드',
  '데이터',
  '모바일',
  '데이터베이스',
  '데브옵스',
  '테스팅툴',
];

const StatisticsListPage = () => {
  const { queryParams, urlSearchParams, setQueryParams } =
    useReplaceQueryParams();

  const [category, setCategory] = useState('');

  useLayoutEffect(() => {
    setCategory(queryParams.get('category') || CATEGORY_LIST[0]);
  }, []);

  useEffect(() => {
    mixpanelTrack('StatisticsPage', 'Viewed', { category: category });
    useScrollInit();

    urlSearchParams.set('category', category);
    setQueryParams(urlSearchParams);
  }, [category]);

  useEffect(() => {
    sendGTMEvent({ event: 'page__view' });
  }, []);

  return (
    <>
      <PageHeader
        title={'스택 인기도'}
        tooltipContent={
          '* 해당 정보는 코드너리 데이터베이스에 등록된 스타트업들을 기준으로 작성되었습니다.'
        }
      />
      <CategorySelectableOption category={category} setCategory={setCategory} />
      <ChartList category={category} />
    </>
  );
};

export default StatisticsListPage;

const CategorySelectableOption = ({ category, setCategory }) => {
  const { dark } = isDark();
  const { classes } = useStatisticsListStyles({ dark });

  const changeStatisticsInfo = (selectedCategory: string) => {
    setCategory(selectedCategory);
    mixpanelTrack('StatisticsPage_CategoryButton', 'Clicked', {
      category: selectedCategory,
    });
  };

  return (
    // 데스크탑은 Tab Bar / 모바일은 Select 로 보여줌
    <>
      <SegmentedControl
        color={'blue'}
        value={category}
        transitionDuration={0}
        onChange={changeStatisticsInfo}
        className={classes.resultTabItem}
        classNames={{
          controlActive: classes.resultTabActiveItem,
        }}
        data={CATEGORY_LIST}
        defaultValue={CATEGORY_LIST[0]}
      />
      <Flex className={classes.mobileTab} gap={6}>
        {CATEGORY_LIST.map((item) => (
          <Button
            key={item}
            className={
              category === item
                ? classes.mobileTabSelectedItem
                : classes.mobileTabNormalItem
            }
            onClick={() => {
              changeStatisticsInfo(item);
            }}
          >
            {item}
          </Button>
        ))}
      </Flex>
    </>
  );
};

const ChartList = ({ category }) => {
  const { data, isLoading } = useGetStatisticsWithCategory(
    category || CATEGORY_LIST[0]
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  const chartData = data?.name !== 'AxiosError' ? groupBy(data, 'label') : {};

  if (isLoading) return <Spinner />;

  return (
    <Grid gutter={20} columns={24}>
      {Object.entries(chartData).map(
        ([key, value]: [any, any]) =>
          value.length > 2 && (
            <Grid.Col
              key={key}
              xl={12}
              lg={12}
              md={12}
              sm={24}
              xs={24}
              className={'fadeInUpAnimation'}
            >
              <StatisticsCard key={key} title={key} data={value} />
            </Grid.Col>
          )
      )}
    </Grid>
  );
};
